import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
// v0.19.22 — 后端错误文案的**共用入口** (三级兜底, 与本文件历史手抄版逐字等价)
import { errReason } from '../utils/FormPrimitives';

/**
 * EditStudentModal — 编辑学员 modal (v0.6.28 拍板, 辉哥 9/29 12:55)
 *
 * 模式: extends Modal (Flarum 2.0 ModalManager 单例)
 *
 * 设计:
 *   - mobile: 从底部弹起 sheet, 5 个字段垂直堆叠 (username 禁用置顶)
 *   - desktop: 540px 居中, 2 列 grid (用户名禁用 + 昵称 + 邮箱 + 手机号 + 校区只读)
 *
 * 字段:
 *   - 用户名 (disabled, 不可改 — 辉哥 12:55 拍板, 用户唯一标识)
 *   - 昵称 (选填)
 *   - 邮箱 (必填)
 *   - 手机号 (必填, 11 位中国手机号)
 *   - 所属校区 (只读, 跟 AddStudentModal 一致, 走 activeSchoolId)
 *
 * 不展示:
 *   - ❌ 绑卡下拉菜单 (辉哥 12:55 拍板 — 绑卡走 BindCardModal)
 *   - ❌ 自动绑组提示 (学员已存在, group_user 已绑定)
 *
 * API: PATCH /api/ziiven/scheduler/students/{id}
 *   - Controller: UpdateStudentController (v0.6.28 加 phone/nickname/school_id 支持)
 *   - 注: extend.php:151 注册的路由是 /students/{id} (不是 /users/{id})
 *
 * 失败 / 成功后:
 *   - 成功 → onCloseCb() + this.hide() + 父组件 reload
 *   - 失败 → alert(error.message) 保持 modal 打开
 */
export default class EditStudentModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);
    const student = vnode.attrs.student;
    this.student = student;
    this.activeSchoolId = vnode.attrs.activeSchoolId;
    this.schools = vnode.attrs.schools || [];
    this.onCloseCb = vnode.attrs.onClose;
    this.saving = false;
    this.form = {
      // username 不在 form 里 (前端 input disabled + 后端 UpdateStudentController 防 curl 强制忽略)
      nickname: student.nickname || '',
      email: student.email || '',
      phone: student.phone || ''
    };
  }
  className() {
    // 跟 AddStudentModal 同款 (v0.5.8)
    return 'Modal--medium Modal--fullscreen-padded ZS-EditStudentModal';
  }
  title() {
    return m("span", null, m("i", {
      className: "fas fa-note-sticky",
      style: "color:var(--zs-cyan-06b6d4, #06b6d4);margin-right:8px;"
    }), app.translator.trans('ziven-scheduler.forum.student-list.edit-title'));
  }
  currentSchoolName() {
    // 跟 AddStudentModal 同款
    const s = this.schools.find(x => String(x.id) === String(this.activeSchoolId));
    return s ? s.name : app.translator.trans('ziven-scheduler.forum.school-dropdown.select-school');
  }
  async submit() {
    if (this.saving) return;

    // 前端必填校验 (服务器再校验一次, 防直接 curl)
    if (!this.form.email || this.form.email.trim() === '') {
      alert(app.translator.trans('ziven-scheduler.lib.error-student-email-required'));
      return;
    }
    if (!this.form.phone || this.form.phone.trim() === '') {
      alert(app.translator.trans('ziven-scheduler.lib.error-student-phone-required'));
      return;
    }
    try {
      this.saving = true;
      m.redraw();

      // v0.6.28 — PATCH 不传 username (前端 input disabled + 后端 UpdateStudentController 防 curl 直改)
      const payload = {
        nickname: this.form.nickname.trim(),
        email: this.form.email.trim(),
        phone: this.form.phone.trim()
      };
      const r = await app.request({
        method: 'PATCH',
        url: app.forum.attribute('apiUrl') + '/ziiven/scheduler/students/' + this.student.id,
        body: payload
      });
      this.saving = false;
      if (this.onCloseCb) this.onCloseCb(r);
      this.hide();
    } catch (e) {
      this.saving = false;
      m.redraw();
      // 跟 AddStudentModal 同款 OR 链 (v0.6.23a 验证过 OR 链 1 工作正常)
      const msg = errReason(e, app.translator.trans('ziven-scheduler.lib.error-unknown'));
      alert(msg);
    }
  }
  content() {
    return m("div", null, m("div", {
      className: "Modal-body"
    }, m("div", {
      className: "ZS-add-form-grid"
    }, m("div", {
      className: "ZS-form-group ZS-form-group-full"
    }, m("div", {
      className: "ZS-form-label"
    }, app.translator.trans('ziven-scheduler.forum.student-list.edit-username-label')), m("input", {
      className: "ZS-form-input",
      value: this.student.username,
      disabled: true,
      readonly: true
    })), m("div", {
      className: "ZS-form-group"
    }, m("div", {
      className: "ZS-form-label"
    }, app.translator.trans('ziven-scheduler.forum.student-list.add-nickname')), m("input", {
      className: "ZS-form-input",
      placeholder: app.translator.trans('ziven-scheduler.forum.student-list.add-nickname-placeholder'),
      value: this.form.nickname,
      oninput: e => {
        this.form.nickname = e.target.value;
      }
    })), m("div", {
      className: "ZS-form-group"
    }, m("div", {
      className: "ZS-form-label"
    }, app.translator.trans('ziven-scheduler.forum.student-list.add-email'), m("span", {
      className: "req"
    }, "*")), m("input", {
      className: "ZS-form-input",
      type: "email",
      placeholder: app.translator.trans('ziven-scheduler.forum.student-list.add-email-placeholder'),
      value: this.form.email,
      oninput: e => {
        this.form.email = e.target.value;
      }
    })), m("div", {
      className: "ZS-form-group"
    }, m("div", {
      className: "ZS-form-label"
    }, app.translator.trans('ziven-scheduler.forum.student-list.add-phone'), m("span", {
      className: "req"
    }, "*")), m("input", {
      className: "ZS-form-input",
      type: "tel",
      inputmode: "numeric",
      placeholder: app.translator.trans('ziven-scheduler.forum.student-list.add-phone-placeholder'),
      value: this.form.phone,
      oninput: e => {
        this.form.phone = e.target.value;
      }
    })), m("div", {
      className: "ZS-form-group ZS-form-group-full"
    }, m("div", {
      className: "ZS-form-label"
    }, app.translator.trans('ziven-scheduler.forum.student-list.add-school')), m("div", {
      className: "ZS-school-display"
    }, m("i", {
      className: "fas fa-house",
      "aria-hidden": "true"
    }), m("span", null, app.translator.trans('ziven-scheduler.forum.student-list.add-school-readonly'), ' ', m("strong", null, this.currentSchoolName())))))), m("div", {
      className: "Modal-footer"
    }, m("button", {
      className: "Button",
      onclick: () => this.hide()
    }, app.translator.trans('ziven-scheduler.forum.student-list.add-cancel')), m("button", {
      className: "Button Button--primary",
      disabled: this.saving,
      onclick: () => this.submit()
    }, this.saving ? app.translator.trans('ziven-scheduler.forum.student-list.add-submitting') : app.translator.trans('ziven-scheduler.forum.student-list.edit-submit'))));
  }
}
flarum.reg.add('ziiven-ziven-scheduler', 'forum/modal/EditStudentModal', EditStudentModal);